import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

interface PendingPayment {
  id: string;
  quantity: number;
  price: number;
  total_amount: number;
  created_at: string;
  status: string;
  ticker?: {
    id: string;
    symbol: string;
    name: string;
  };
}

interface UsePendingPaymentCheckResult {
  pendingPayments: PendingPayment[];
  loading: boolean;
  hasUrgentPayment: boolean;
  urgentPayment: PendingPayment | null;
  dismissBanner: () => void;
  isBannerDismissed: boolean;
  refetch: () => Promise<void>;
}

const DISMISS_STORAGE_KEY = "swapx_pending_payment_dismissed";
const DISMISS_DURATION_HOURS = 4;

export function usePendingPaymentCheck(): UsePendingPaymentCheckResult {
  const [pendingPayments, setPendingPayments] = useState<PendingPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);

  // Check if banner was recently dismissed
  useEffect(() => {
    const dismissedAt = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (dismissedAt) {
      const dismissTime = new Date(dismissedAt).getTime();
      const now = Date.now();
      const hoursSinceDismiss = (now - dismissTime) / (1000 * 60 * 60);
      
      if (hoursSinceDismiss < DISMISS_DURATION_HOURS) {
        setIsBannerDismissed(true);
      } else {
        localStorage.removeItem(DISMISS_STORAGE_KEY);
      }
    }
  }, []);

  const fetchPendingPayments = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user?.id) {
        setPendingPayments([]);
        setLoading(false);
        return;
      }

      // Query trades where user is buyer with pending payment status
      const { data: trades, error } = await supabase
        .from("trades")
        .select(`
          id,
          quantity,
          price,
          total_amount,
          created_at,
          status,
          ticker:tickers (
            id,
            symbol,
            name
          )
        `)
        .eq("buyer_id", session.user.id)
        .in("status", ["PENDING_PAYMENT", "MATCHED"])
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching pending payments:", error);
        setPendingPayments([]);
      } else {
        setPendingPayments(
          (trades || []).map((t) => ({
            ...t,
            ticker: Array.isArray(t.ticker) ? t.ticker[0] : t.ticker,
          }))
        );
      }
    } catch (err) {
      console.error("Pending payment check error:", err);
      setPendingPayments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPendingPayments();

    // Subscribe to auth changes to refetch when user logs in
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === "SIGNED_IN") {
        fetchPendingPayments();
      } else if (event === "SIGNED_OUT") {
        setPendingPayments([]);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchPendingPayments]);

  // Check for urgent payments (created more than 20 hours ago - 4 hours until timeout)
  const urgentPayment = pendingPayments.find((p) => {
    const createdAt = new Date(p.created_at).getTime();
    const now = Date.now();
    const hoursSinceCreated = (now - createdAt) / (1000 * 60 * 60);
    return hoursSinceCreated >= 20; // 4 hours until 24-hour timeout
  }) || null;

  const dismissBanner = useCallback(() => {
    localStorage.setItem(DISMISS_STORAGE_KEY, new Date().toISOString());
    setIsBannerDismissed(true);
  }, []);

  return {
    pendingPayments,
    loading,
    hasUrgentPayment: !!urgentPayment,
    urgentPayment,
    dismissBanner,
    isBannerDismissed,
    refetch: fetchPendingPayments,
  };
}
